import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  CommunicationAutomation,
  CommunicationAutomationUpsert,
  SurveyForm,
  createCommunicationAutomation,
  deleteCommunicationAutomation,
  enqueueCommunicationAutomation,
  listCommunicationAutomations,
  listSurveyForms,
  updateCommunicationAutomation,
} from '../../services/customerSuccessEngagementApi';
import { listCompanies, Company } from '../../services/coreCompaniesApi';
import { EmailTemplate, listEmailTemplates } from '../../services/emailTemplatesApi';
import { CommunicationRole, listCommunicationRoles } from '../../services/communicationRolesApi';

const TRIGGERS = [
  { value: 'Manual', label: 'Manual' },
  { value: 'ContractCreated', label: 'Contrato criado' },
  { value: 'TicketResolved', label: 'Chamado resolvido' },
  { value: 'RecurringActiveContract', label: 'Recorrente (contrato ativo)' },
];

const FREQUENCIES = [
  { value: '', label: '—' },
  { value: 'Daily', label: 'Diária' },
  { value: 'Weekly', label: 'Semanal' },
  { value: 'Monthly', label: 'Mensal' },
  { value: 'Yearly', label: 'Anual' },
];

const emptyForm: CommunicationAutomationUpsert = {
  name: '',
  emailTemplateId: '',
  triggerType: 'Manual',
  delayMinutes: 0,
  frequency: null,
  surveyFormId: null,
  companyId: null,
  communicationRoleId: null,
  includeTicketContact: false,
  isEnabled: true,
};

export function CommunicationAutomationsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('customer-success.engagement');
  const [items, setItems] = useState<CommunicationAutomation[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [forms, setForms] = useState<SurveyForm[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [roles, setRoles] = useState<CommunicationRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CommunicationAutomationUpsert>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();

  const activeTemplates = useMemo(
    () => templates.filter((item) => item.isActive),
    [templates],
  );

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const [list, templateList, formList, companyList, roleList] = await Promise.all([
        listCommunicationAutomations(),
        listEmailTemplates('customer-success').catch(() => [] as EmailTemplate[]),
        listSurveyForms().catch(() => [] as SurveyForm[]),
        listCompanies({ activeOnly: true, pageSize: 500 }).catch(() => ({ items: [] as Company[] })),
        listCommunicationRoles().catch(() => [] as CommunicationRole[]),
      ]);
      setItems(list);
      setTemplates(templateList);
      setForms(formList);
      setCompanies(companyList.items);
      setRoles(roleList);
    } catch {
      toast.error('Falha ao carregar automações.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  function startCreate(): void {
    setEditingId(null);
    setForm({
      ...emptyForm,
      emailTemplateId: activeTemplates[0]?.id ?? '',
    });
    expandForm();
  }

  function startEdit(item: CommunicationAutomation): void {
    setEditingId(item.id);
    setForm({
      name: item.name,
      emailTemplateId: item.emailTemplateId,
      triggerType: item.triggerType,
      delayMinutes: item.delayMinutes,
      frequency: item.frequency ?? null,
      surveyFormId: item.surveyFormId ?? null,
      companyId: item.companyId ?? null,
      communicationRoleId: item.communicationRoleId ?? null,
      includeTicketContact: item.includeTicketContact,
      isEnabled: item.isEnabled,
    });
    expandForm();
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !form.name.trim() || !form.emailTemplateId) return;
    setSaving(true);
    try {
      const payload: CommunicationAutomationUpsert = {
        ...form,
        name: form.name.trim(),
        frequency: form.triggerType === 'RecurringActiveContract' ? form.frequency || 'Monthly' : null,
        surveyFormId: form.surveyFormId || null,
        companyId: form.companyId || null,
        delayMinutes: Math.max(0, form.delayMinutes || 0),
      };
      if (editingId) {
        await updateCommunicationAutomation(editingId, payload);
        toast.success('Automação atualizada.');
        collapseForm(() => {
          setEditingId(null);
          setForm(emptyForm);
        });
      } else {
        await createCommunicationAutomation(payload);
        toast.success('Automação criada.');
        handleCreateSuccess(() => {
          setEditingId(null);
          setForm(emptyForm);
        });
      }
      setEditingId(null);
      await loadData();
    } catch {
      toast.error('Falha ao salvar automação.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: CommunicationAutomation): Promise<void> {
    if (!canWrite) return;
    const ok = await confirm({
      title: 'Remover automação',
      message: `Remover "${item.name}"?`,
    });
    if (!ok) return;
    try {
      await deleteCommunicationAutomation(item.id);
      toast.success('Automação removida.');
      await loadData();
    } catch {
      toast.error('Falha ao remover.');
    }
  }

  async function handleEnqueue(item: CommunicationAutomation): Promise<void> {
    if (!canWrite) return;
    try {
      await enqueueCommunicationAutomation(item.id, {
        companyId: item.companyId ?? undefined,
      });
      toast.success('Disparo enfileirado. O job de jornadas processará o envio.');
    } catch {
      toast.error('Falha ao enfileirar disparo.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Automações"
        description="Gatilhos, atraso, frequência, empresa elegível e pesquisa opcional vinculados a um template de e-mail."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/customer-success/settings?tab=emails" className="erp-btn-secondary">
              Templates de e-mail
            </Link>
            {canWrite ? (
              <button
                type="button"
                className="erp-btn-primary"
                onClick={startCreate}
                disabled={!activeTemplates.length}
              >
                Nova automação
              </button>
            ) : null}
          </div>
        }
      />

      {!activeTemplates.length ? (
        <p className="mb-4 text-sm text-amber-700">
          Cadastre ao menos um template em{' '}
          <Link to="/customer-success/settings?tab=emails" className="underline">
            Configurações → E-mails
          </Link>{' '}
          antes de criar automações.
        </p>
      ) : null}

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar automação' : 'Nova automação'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(() => { setEditingId(null); setForm(emptyForm); })}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form-grid">
            <label className="erp-label erp-form-grid-full">
              Nome <RequiredMark />
              <input
                className="erp-input"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </label>
            <label className="erp-label">
              Template de e-mail <RequiredMark />
              <select
                className="erp-input"
                value={form.emailTemplateId}
                onChange={(event) => setForm({ ...form, emailTemplateId: event.target.value })}
                required
              >
                <option value="">Selecione</option>
                {activeTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.kind})
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Gatilho <RequiredMark />
              <select
                className="erp-input"
                value={form.triggerType}
                onChange={(event) => setForm({ ...form, triggerType: event.target.value })}
              >
                {TRIGGERS.map((trigger) => (
                  <option key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Atraso (minutos)
              <input
                className="erp-input"
                type="number"
                min={0}
                value={form.delayMinutes}
                onChange={(event) =>
                  setForm({ ...form, delayMinutes: Number(event.target.value) || 0 })
                }
              />
            </label>
            {form.triggerType === 'RecurringActiveContract' ? (
              <label className="erp-label">
                Frequência
                <select
                  className="erp-input"
                  value={form.frequency ?? 'Monthly'}
                  onChange={(event) => setForm({ ...form, frequency: event.target.value })}
                >
                  {FREQUENCIES.filter((item) => item.value).map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="erp-label">
              Empresa (opcional)
              <select
                className="erp-input"
                value={form.companyId ?? ''}
                onChange={(event) => setForm({ ...form, companyId: event.target.value || null })}
              >
                <option value="">Todas elegíveis</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label">
              Quem recebe
              <select
                className="erp-input"
                value={form.communicationRoleId ?? ''}
                onChange={(event) =>
                  setForm({ ...form, communicationRoleId: event.target.value || null })
                }
              >
                <option value="">Contato do chamado ou principal</option>
                {roles.filter((role) => role.isSystem).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </label>
            {form.communicationRoleId && form.triggerType === 'TicketResolved' ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!form.includeTicketContact}
                  onChange={(event) =>
                    setForm({ ...form, includeTicketContact: event.target.checked })
                  }
                />
                Incluir também o contato do chamado
              </label>
            ) : null}
            <label className="erp-label">
              Formulário de pesquisa (opcional)
              <select
                className="erp-input"
                value={form.surveyFormId ?? ''}
                onChange={(event) => setForm({ ...form, surveyFormId: event.target.value || null })}
              >
                <option value="">Nenhum</option>
                {forms.map((surveyForm) => (
                  <option key={surveyForm.id} value={surveyForm.id}>
                    {surveyForm.name} ({surveyForm.surveyType})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isEnabled}
                onChange={(event) => setForm({ ...form, isEnabled: event.target.checked })}
              />
              Habilitada
            </label>
            <FormActionBar
              className="erp-btn-actions erp-form-grid-full"
              onCancel={() => toggleOrCancel(() => { setEditingId(null); setForm(emptyForm); })}
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <p className="text-sm text-secondary-500">Nenhuma automação cadastrada.</p>
      ) : (
        <ul className="divide-y divide-secondary-100 rounded-xl border border-secondary-200 bg-white">
          {items.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="font-medium text-secondary-900">
                  {item.name}
                  {!item.isEnabled ? (
                    <span className="ml-2 text-xs font-normal text-secondary-500">(desligada)</span>
                  ) : null}
                </p>
                <p className="mt-1 text-sm text-secondary-600">
                  {item.emailTemplateName ?? 'Template'} · {item.triggerType}
                  {item.delayMinutes > 0 ? ` · +${item.delayMinutes} min` : ''}
                  {item.frequency ? ` · ${item.frequency}` : ''}
                  {item.companyName ? ` · ${item.companyName}` : ' · todas empresas'}
                  {item.communicationRoleName ? ` · papel: ${item.communicationRoleName}` : ''}
                  {item.surveyFormName ? ` · pesquisa: ${item.surveyFormName}` : ''}
                </p>
              </div>
              {canWrite ? (
                <div className="flex shrink-0 items-center gap-1">
                  {item.triggerType === 'Manual' || item.isEnabled ? (
                    <button
                      type="button"
                      className="erp-btn-secondary px-2 py-1 text-xs"
                      onClick={() => void handleEnqueue(item)}
                    >
                      Disparar
                    </button>
                  ) : null}
                  <EditIconButton onClick={() => startEdit(item)} />
                  <DeleteIconButton onClick={() => void handleDelete(item)} />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
}
